import * as React from "react";
import { useAuthContext } from "@/contexts/AuthContext";
import { organizationRepository } from "@/data/repositories";
import type { CreateOrganizationInput, UpdateOrganizationInput } from "@/data/repositories/organizationRepository";
import type { OrganizationV2 } from "@/types/domain/organization";
import type { Organization } from "@/types/organization";

function toOrganization(organization: OrganizationV2): Organization {
  return {
    id: organization.id,
    name: organization.name,
    slug: organization.slug,
    logoUrl: organization.logoUrl,
    plan: organization.subscriptionPlan,
    subscriptionStatus: organization.subscriptionStatus,
    createdAt: organization.createdAt,
  };
}

interface OrganizationContextValue {
  organizations: Organization[];
  activeOrganization: Organization | null;
  isLoading: boolean;
  error: string | null;
  setActiveOrganizationId: (id: string) => void;
  createOrganization: (input: Omit<CreateOrganizationInput, "createdBy">) => Promise<Organization>;
  updateOrganization: (id: string, patch: UpdateOrganizationInput) => Promise<Organization>;
  deleteOrganization: (id: string) => Promise<void>;
}

const OrganizationContext = React.createContext<OrganizationContextValue | undefined>(undefined);

const ACTIVE_ORG_KEY = "atelier.active-organization-id";

const slugify = (value: string) =>
  value.toLowerCase().trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/**
 * Scopes the entire app to one Organization (tenant) at a time.
 *
 * A user can belong to many organizations (agency staff working across
 * client accounts, or a planner running their own studio plus consulting
 * for another). Every data-fetching hook further down the tree should key
 * its queries off `activeOrganization.id` once real data replaces the
 * dummy fixtures — this is the single place that boundary is decided.
 */
export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthContext();
  const [organizations, setOrganizations] = React.useState<Organization[]>([]);
  const [activeOrganizationId, setActiveOrganizationIdState] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated || !user) {
      setOrganizations([]);
      setActiveOrganizationIdState(null);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    setIsLoading(true);
    setError(null);
    organizationRepository
      .listForUser(user.id)
      .then(async (rows) => {
        if (rows.length > 0 || !user.organizationName?.trim()) return rows;

        // Supabase Auth stores the workspace name during sign-up. Provision
        // the first tenant only when the authenticated user has no active
        // membership, keeping every new account isolated from demo data.
        const name = user.organizationName.trim();
        const baseSlug = slugify(name) || "workspace";
        const created = await organizationRepository.create({
          name,
          slug: `${baseSlug}-${user.id.slice(0, 6)}`,
          organizationType: "independent_planner",
          timezone: "America/Mexico_City",
          currency: "MXN",
          countryCode: "MX",
          createdBy: user.id,
        });
        return [created];
      })
      .then((rows) => {
        if (cancelled) return;
        const loaded = rows.map(toOrganization);
        setOrganizations(loaded);
        const stored = window.localStorage.getItem(ACTIVE_ORG_KEY);
        const initial = loaded.find((organization) => organization.id === stored) ?? loaded[0];
        setActiveOrganizationIdState(initial?.id ?? null);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : "Unable to load organizations");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user]);

  const setActiveOrganizationId = React.useCallback((id: string) => {
    window.localStorage.setItem(ACTIVE_ORG_KEY, id);
    setActiveOrganizationIdState(id);
  }, []);

  const activeOrganization = React.useMemo(
    () => organizations.find((o) => o.id === activeOrganizationId) ?? null,
    [organizations, activeOrganizationId]
  );

  const createOrganization = React.useCallback(
    async (input: Omit<CreateOrganizationInput, "createdBy">): Promise<Organization> => {
      if (!user) throw new Error("You must be signed in to create an organization");
      setError(null);
      const created = toOrganization(await organizationRepository.create({ ...input, createdBy: user.id }));
      setOrganizations((current) => [...current, created]);
      setActiveOrganizationId(created.id);
      return created;
    },
    [user, setActiveOrganizationId]
  );

  const updateOrganization = React.useCallback(
    async (id: string, patch: UpdateOrganizationInput): Promise<Organization> => {
      setError(null);
      const updated = toOrganization(await organizationRepository.update(id, patch));
      setOrganizations((current) => current.map((organization) => (organization.id === id ? updated : organization)));
      return updated;
    },
    []
  );

  const deleteOrganization = React.useCallback(async (id: string): Promise<void> => {
    setError(null);
    try {
      await organizationRepository.delete(id);
      setOrganizations((current) => {
        const remaining = current.filter((organization) => organization.id !== id);
        if (activeOrganizationId === id) {
          const nextId = remaining[0]?.id ?? null;
          setActiveOrganizationIdState(nextId);
          if (nextId) window.localStorage.setItem(ACTIVE_ORG_KEY, nextId);
          else window.localStorage.removeItem(ACTIVE_ORG_KEY);
        }
        return remaining;
      });
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "Unable to delete organization";
      setError(message);
      throw cause;
    }
  }, [activeOrganizationId]);

  const value = React.useMemo<OrganizationContextValue>(
    () => ({ organizations, activeOrganization, isLoading, error, setActiveOrganizationId, createOrganization, updateOrganization, deleteOrganization }),
    [organizations, activeOrganization, isLoading, error, setActiveOrganizationId, createOrganization, updateOrganization, deleteOrganization]
  );

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

export function useOrganizationContext(): OrganizationContextValue {
  const ctx = React.useContext(OrganizationContext);
  if (!ctx) throw new Error("useOrganizationContext must be used within an OrganizationProvider");
  return ctx;
}
